import { DEFAULT_ROW_HEIGHT } from "@/constants";
import { useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef, useState, useCallback } from "react";

const MAX_ANCESTOR_OBSERVE_DEPTH = 10;
/**
 * Tracks the document offset of a table body element for window-based virtualization.
 * Updates when layout changes affect the element's position.
 */
const useTableBodyDocumentOffset = (
  elementRef: React.ForwardedRef<HTMLDivElement>,
) => {
  const [offset, setOffset] = useState(0);
  const lastOffsetRef = useRef<number>(0);
  const updatePendingRef = useRef(false);

  const calculateOffset = useCallback(() => {
    if (!elementRef || !("current" in elementRef) || !elementRef.current) {
      return 0;
    }

    const viewportTop = elementRef.current.getBoundingClientRect().top;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return viewportTop + scrollTop;
  }, [elementRef]);

  useEffect(() => {
    if (!elementRef || !("current" in elementRef) || !elementRef.current) {
      return;
    }

    const updateOffset = () => {
      if (updatePendingRef.current) return;
      updatePendingRef.current = true;

      requestAnimationFrame(() => {
        const newOffset = calculateOffset();

        if (lastOffsetRef.current !== newOffset) {
          lastOffsetRef.current = newOffset;
          setOffset(newOffset);
        }

        updatePendingRef.current = false;
      });
    };

    // Initial calculation
    updateOffset();

    // Observe element and ancestors for layout changes
    const resizeObserver = new ResizeObserver(() => {
      updateOffset();
    });

    resizeObserver.observe(elementRef.current);

    // Observe ancestors to catch collapsible sections, etc.
    let element = elementRef.current.parentElement;
    let depth = 0;

    while (
      element &&
      element !== document.body &&
      depth < MAX_ANCESTOR_OBSERVE_DEPTH
    ) {
      resizeObserver.observe(element);
      element = element.parentElement;
      depth++;
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [elementRef, calculateOffset]);

  return offset;
};

/**
 * Creates a virtualizer for table rows, using window or container scrolling.
 *
 * @param count - Number of rows to virtualize
 * @param tableBodyRef - Reference to the tbody element - used to compute offset.
 * @param scrollElementRef - Reference to the scrolling element - used when isDialog=true. Defaults to tableBodyRef
 * @param isInDialog - Use container scrolling (true) vs window scrolling (false)
 */
const useRowVirtualizer = (
  count: number,
  tableBodyRef: React.ForwardedRef<HTMLDivElement>,
  scrollElementRef?: React.ForwardedRef<HTMLDivElement>,
  isInDialog = false,
) => {
  const documentOffset = useTableBodyDocumentOffset(tableBodyRef);

  // For dialogs, use container-based scrolling
  const inDialogVirtualizer = useVirtualizer({
    count,
    estimateSize: () => DEFAULT_ROW_HEIGHT,
    getScrollElement: () => {
      const ref = scrollElementRef ?? tableBodyRef;
      return ref && "current" in ref ? ref.current : null;
    },
    enabled: isInDialog,
  });

  // For pages, use window-based scrolling with calculated offset
  const virtualizer = useWindowVirtualizer({
    count,
    estimateSize: () => DEFAULT_ROW_HEIGHT,
    scrollMargin: documentOffset,
    enabled: !isInDialog,
  });

  return isInDialog ? inDialogVirtualizer : virtualizer;
};

export default useRowVirtualizer;
